<template>
  <section class="customer-detail-multi-customer">
    <article class="customer-detail-card customer-detail-card--accent-primary">
      <SalesToolbar
        mode="multi-customer"
        :show-customer-filter="false"
        :initial-date-preset-key="''"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />

      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />

      <SalesTable
        :rows="paginatedRows"
        :loading="effectiveLoading"
        :empty-text="emptyText"
        :pagination="salesPagination"
        :show-customer-column="false"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @change-sale-status="handleChangeSaleStatus"
        @edit-sale="handleEditSale"
        @delete-sale="handleDeleteSale"
      />

      <SaleModal
        :show="showSaleEditModal"
        :mode="saleModalMode"
        :initial-data="saleModalInitialData"
        customer-mode="multi"
        :submitting="saleSubmitting"
        :advertising-enabled="isAdvertisingModuleActive"
        @close="closeSaleModal"
        @delete="handleDeleteSaleFromModal"
        @submit="submitSaleEdit"
      />

      <ConfirmActionModal
        :show="isDeleteConfirmModalOpen"
        :title="multiCustomerText.deleteModal.title"
        :subtitle="multiCustomerText.deleteModal.subtitle"
        :message="deleteConfirmMessage"
        :confirm-text="multiCustomerText.deleteModal.confirm"
        @cancel="closeDeleteConfirmModal"
        @confirm="confirmDeleteSale"
      />

      <SaleStatusModal
        :show="isStatusModalOpen"
        :sale-name="resolveSelectedSaleName()"
        :current-status-label="resolveSelectedSaleStatusLabel()"
        :statuses="statusOptions"
        :selected-status-id="resolveSelectedSaleStatusId()"
        :submitting="statusModalSubmitting"
        @close="closeStatusModal"
        @submit="confirmUpdateSaleStatus"
      />
    </article>
  </section>
</template>

<script>
import SalesToolbar from '@/components/sales/shared/SalesToolbar.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import SalesTable from '@/components/sales/shared/SalesTable.vue';
import SaleStatusModal from '@/components/sales/shared/SaleStatusModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import salesService from '@/services/sales';
import {
  createDefaultSalesMultiCustomerToolbarFilters,
  normalizeSalesMultiCustomerToolbarFilters,
  buildSalesMultiCustomerListParams,
} from '@/composables/salesMultiCustomer/useSalesMultiCustomerFilters';
import { buildUpdateSaleMultiCustomerRequestData } from '@/composables/salesMultiCustomer/useSalesMultiCustomerCreateModal';
import { fetchSalesMultiCustomerRows } from '@/composables/salesMultiCustomer/useSalesMultiCustomerList';
import {
  buildSalesMultiCustomerStatusTabs,
  filterSalesMultiCustomerRowsByStatus,
} from '@/composables/salesMultiCustomer/useSalesMultiCustomerStatusTabs';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createCustomerDetailSaleActionState,
  createCustomerDetailSaleActionComputed,
  createCustomerDetailSaleActionMethods,
} from '@/composables/customers/customerDetail/useCustomerDetailSaleActions';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';

const { catalog: text } = useDomainText('sales');
const multiCustomerText = text?.customerDetail?.multiCustomer || {
  entityLabel: 'Negocio multicliente',
  emptyText: 'Sin negocios multicliente disponibles para los filtros seleccionados.',
  deleteModal: {
    title: 'Eliminar negocio multicliente',
    subtitle: 'Esta accion eliminara el negocio multicliente seleccionado.',
    confirm: 'Eliminar',
  },
  actionMessages: {
    loadStatusOptionsError: 'No fue posible cargar los estados de negocio.',
    updateStatusError: 'No fue posible actualizar el estado del negocio multicliente.',
    updateStatusSuccess: 'El estado del negocio multicliente ha sido actualizado correctamente.',
    updateError: 'No fue posible actualizar el negocio multicliente.',
    updateSuccess: 'El negocio multicliente ha sido actualizado correctamente.',
    loadEditError: 'No fue posible cargar el negocio para edicion.',
    deleteError: 'No fue posible eliminar el negocio multicliente.',
    deleteSuccess: 'El negocio multicliente ha sido eliminado correctamente.',
  },
};

const saleActionComputed = createCustomerDetailSaleActionComputed({
  entityLabel: multiCustomerText.entityLabel,
});

const saleActionMethods = createCustomerDetailSaleActionMethods({
  readService: salesService,
  writeService: salesMultiCustomerService,
  buildUpdateRequestData: buildUpdateSaleMultiCustomerRequestData,
  messages: multiCustomerText.actionMessages,
});

export default {
  name: 'CustomerDetailMultiCustomerTab',
  components: {
    SalesToolbar,
    StatusTabs,
    SalesTable,
    SaleStatusModal,
    SaleModal,
    ConfirmActionModal,
  },
  emits: ['search-change', 'filters-change', 'view-sale-detail', 'change-sale-status', 'update-count'],
  props: {
    customer: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: multiCustomerText.emptyText,
    },
  },
  computed: {
    customerId() {
      return Number(this.customer?.id || 0);
    },
    multiCustomerText() {
      return multiCustomerText;
    },
    ...saleActionComputed,
    effectiveLoading() {
      return this.loading || this.tableLoading;
    },
    normalizedRows() {
      return Array.isArray(this.salesRows) ? this.salesRows : [];
    },
    statusTabs() {
      return buildSalesMultiCustomerStatusTabs(this.normalizedRows);
    },
    filteredRows() {
      return filterSalesMultiCustomerRowsByStatus(this.normalizedRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.filteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        sale_date: (row) => new Date(row?.sale_date || row?.date || 0).getTime(),
        customer: (row) => String(row?.company_name || row?.customer_name || row?.cliente || '').trim().toLowerCase(),
        sale_name: (row) => String(row?.sale_name || row?.project || row?.name || row?.code || '').trim().toLowerCase(),
        service: (row) => String(row?.service_name || row?.service || row?.service_type || '').trim().toLowerCase(),
        subtotal: (row) => Number(row?.subtotal || 0),
        balance: (row) => Number(row?.balance ?? row?.saldo ?? 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    salesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
  },
  data() {
    return {
      tableLoading: false,
      salesRows: [],
      activeStatusTab: 'all',
      toolbarSearch: '',
      toolbarFilters: createDefaultSalesMultiCustomerToolbarFilters(),
      appliedToolbarSearch: '',
      appliedToolbarFilters: createDefaultSalesMultiCustomerToolbarFilters(),
      listRequestToken: 0,
      currentPage: 1,
      pageSize: 30,
      sortField: 'sale_date',
      sortDirection: 'desc',
      lastFetchParamsKey: '',
      hasLoadedListOnce: false,
      ...createCustomerDetailSaleActionState(),
    };
  },
  mounted() {
    this.fetchSales(true);
  },
  watch: {
    customerId(nextId, previousId) {
      if (nextId === previousId) {
        return;
      }

      this.currentPage = 1;
      this.fetchSales(true);
    },
  },
  methods: {
    buildRemoteListParams() {
      const params = buildSalesMultiCustomerListParams({
        toolbarFilters: this.appliedToolbarFilters,
        toolbarSearch: this.appliedToolbarSearch,
      });

      const customerId = Number(this.customerId || 0);
      return {
        ...params,
        customer: customerId > 0 ? [String(customerId)] : [],
      };
    },
    buildFetchParamsKey() {
      return JSON.stringify(this.buildRemoteListParams());
    },
    ensureValidPage() {
      this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
    },
    async fetchSales(force = false) {
      const paramsKey = this.buildFetchParamsKey();

      if (!force && this.tableLoading && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      if (!force && this.hasLoadedListOnce && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      this.lastFetchParamsKey = paramsKey;
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const rows = await fetchSalesMultiCustomerRows(salesMultiCustomerService, this.buildRemoteListParams());

        if (requestToken === this.listRequestToken) {
          this.salesRows = rows;
          this.ensureValidPage();
          this.$emit('update-count', {
            key: 'multi-customer',
            count: this.salesRows.length,
          });
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.salesRows = [];
          this.$emit('update-count', {
            key: 'multi-customer',
            count: 0,
          });
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
          this.hasLoadedListOnce = true;
        }
      }
    },
    handleToolbarSearch(value) {
      const nextSearch = String(value || '');
      this.toolbarSearch = nextSearch;
      this.appliedToolbarSearch = nextSearch;
      this.currentPage = 1;
      this.fetchSales(true);
      this.$emit('search-change', this.toolbarSearch);
    },
    handleToolbarFilters(filters, meta = {}) {
      const previousDateStart = String(this.toolbarFilters?.date_start || '').trim();
      const previousDateEnd = String(this.toolbarFilters?.date_end || '').trim();
      const normalized = normalizeSalesMultiCustomerToolbarFilters(filters);
      this.toolbarFilters = normalized;

      if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
        this.toolbarSearch = String(normalized.search || '');
      }

      const nextDateStart = String(normalized.date_start || '').trim();
      const nextDateEnd = String(normalized.date_end || '').trim();
      const dateChanged = nextDateStart !== previousDateStart || nextDateEnd !== previousDateEnd;
      const trigger = String(meta?.trigger || '').trim();

      if (dateChanged && trigger === 'date') {
        this.appliedToolbarFilters = normalized;
        this.appliedToolbarSearch = String(this.toolbarSearch || '');
        this.currentPage = 1;
        this.fetchSales(true);
      }

      this.$emit('filters-change', {
        ...normalized,
        search: this.toolbarSearch,
      });
    },
    applyToolbarFilters(payload = {}) {
      const search = Object.prototype.hasOwnProperty.call(payload, 'search')
        ? String(payload.search || '')
        : String(this.toolbarSearch || '');

      const filters = Object.prototype.hasOwnProperty.call(payload, 'filters')
        ? normalizeSalesMultiCustomerToolbarFilters(payload.filters)
        : normalizeSalesMultiCustomerToolbarFilters(this.toolbarFilters);

      this.toolbarSearch = search;
      this.toolbarFilters = filters;
      this.appliedToolbarSearch = search;
      this.appliedToolbarFilters = filters;
      this.currentPage = 1;
      this.fetchSales(true);
    },
    handlePageChange(nextPage) {
      const page = Number(nextPage || 1);
      if (!Number.isFinite(page)) {
        return;
      }

      this.currentPage = page;
      this.ensureValidPage();
    },
    handleSortChange(sortPayload) {
      const nextSort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        sortPayload,
        ['sale_date', 'customer', 'sale_name', 'service', 'subtotal', 'balance'],
      );

      if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
        return;
      }

      this.sortField = nextSort.field;
      this.sortDirection = nextSort.direction;
      this.currentPage = 1;
      this.ensureValidPage();
    },
    ...saleActionMethods,
    selectStatusTab(statusKey) {
      this.activeStatusTab = statusKey;
      this.currentPage = 1;
      this.ensureValidPage();
      this.$emit('filters-change', {
        ...this.appliedToolbarFilters,
        search: this.appliedToolbarSearch,
        status_tab: this.activeStatusTab,
      });
    },
  },
};
</script>

<style scoped>
.customer-detail-multi-customer {
  display: grid;
  gap: 12px;
}
</style>
